<!--
@component
Stats

See markup below.
-->

<script lang="ts">
  import type { components } from '$lib/@types/opta.api';
  import Stat from './Stat.svelte';

  type StatDto = components['schemas']['StatDto'];

  // Loose prop type — the upstream `matchStats` shape is intentionally
  // permissive (see `src/lib/@types/opta.ts`). We narrow internally.
  let { stats }: { stats?: unknown } = $props();

  /**
   * Build an O(1) lookup map keyed by stat `type` so we don't repeatedly
   * scan the `stat` array for every metric we want to display.
   */
  const toLookup = (team: unknown): Record<string, StatDto> => {
    const arr =
      team && typeof team === 'object' && 'stat' in team ?
        ((team as { stat?: StatDto[] }).stat ?? [])
      : [];
    return arr.reduce<Record<string, StatDto>>((acc, s) => {
      if (s.type) acc[s.type] = s;
      return acc;
    }, {});
  };

  const teams = $derived(Array.isArray(stats) ? stats : []);
  const home = $derived(toLookup(teams[0]));
  const away = $derived(toLookup(teams[1]));

  /**
   * Single source of truth for which stats render and in what order.
   * Add/remove rows here rather than duplicating template markup.
   */
  type Row = { title: string; type: string; suffix?: string };
  const rows: Row[] = [
    { title: 'Possession', type: 'possessionPercentage', suffix: '%' },
    { title: 'Shots', type: 'totalScoringAtt' },
    { title: 'Shots on target', type: 'ontargetScoringAtt' },
    { title: 'Accurate passes', type: 'accuratePass' },
    { title: 'Corners', type: 'cornerTaken' },
    { title: 'Fouls', type: 'fkFoulLost' },
    { title: 'Tackles won', type: 'wonTackle' },
    { title: 'Saves', type: 'saves' },
  ];

  // Only render the panel once we have the core stats that signal a
  // populated feed (matches previous gating behaviour).
  const hasStats = $derived(
    !!home.possessionPercentage &&
      !!away.possessionPercentage &&
      !!home.accuratePass &&
      !!away.accuratePass
  );
</script>

<div class="stats">
  {#if hasStats}
    <div class="stats-container">
      {#each rows as { title, type, suffix } (type)}
        <Stat {title} {suffix} homeStat={home[type]} awayStat={away[type]} />
      {/each}
    </div>
  {/if}
</div>
